import * as Yup from 'yup';

import {
  contactsSchema,
  contentSchema,
  imageSchema,
  locationSchema,
  scheduleItemSchema,
  seoValidationSchema,
} from 'utils/yup';
import {
  mapAddress,
  mapContacts,
  mapContent,
  mapContentToForm,
  mapGallery,
  mapImage, mapImageForSeo,
  mapSelect,
  mapValuesToAddress,
  mapValuesToSelect
} from "utils/mappings";
import { prepareContent } from 'components/form/Content/utils';
import { Statuses, StatusesEnum } from 'constants/status';
import { IResortDraftItem, IResortItem } from 'store/slices/resorts/interfaces';
import { LangEnum, LangEnumKeys } from 'constants/lang';
import { tutuWidgetName } from 'types/tutu';

import { mapDataToCompact, mapDataToTable } from '../utils';

const mapGetThere = getThereValues => {
  return getThereValues.map(item => ({
    id: item.selectedWay?.value,
    description: item.description,
    tutuWidget: item.selectedWayTutu?.value,
  }));
};

const mapGetThereToForm = (getThereValues, getThereDataValues) => {
  return getThereValues
    .map(item => {
      const getThereValueData = getThereDataValues.find(
        elem => item.id === elem.id
      );

      // If getThereValueData is equal null/undefined, then the value has been removed from the catalog
      if (getThereValueData) {
        return {
          selectedWay: {
            ...getThereValueData,
            value: getThereValueData.id,
            label: getThereValueData.name,
          },
          selectedWayTutu: item.tutuWidget
            ? {
                value: item.tutuWidget,
                label: tutuWidgetName[item.tutuWidget],
              }
            : null,
          description: item.description || '',
        };
      }

      return null;
    })
    .filter(Boolean);
};

export const mapValuesToForm = values => {
  return {
    ...values,
    address: values.address ? mapValuesToAddress(values.address) : {},
    attractions: mapDataToTable(values.attractions) || '',
    category: values.categoryData
      ? mapValuesToSelect(values.categoryData)
      : null,
    content: prepareContent(mapContentToForm(values.content || [])),
    externalUrl: values.externalUrl || '',
    getThere:
      values.getThereData && values.getThere.length > 0
        ? mapGetThereToForm(values.getThere, values.getThereData)
        : [],
    intro: values?.intro || '',
    lang: values.lang || LangEnum.ru,
    metaDescription: values.metaDescription || '',
    metaKeywords: values.metaKeywords || '',
    metaTitle: values.metaTitle || '',
    name: values.name || '',
    ogDescription: values.ogDescription || '',
    ogTitle: values.ogTitle || '',
    hotels: mapDataToTable(values.hotels) || '',
    restaurants: mapDataToTable(values.restaurants) || '',
    shortDescription: values.shortDescription || '',
    sortPriority: values.sortPriority || null,
    sortPriorityMainPage: values.sortPriority || null,
    status:
      values.status === StatusesEnum.DRAFT
        ? StatusesEnum.PUBLISHED
        : values.status,
    tags: values.tagsData ? mapValuesToSelect(values.tagsData) : [],
    ogType: values.ogType || null,
    ogUrl: values.ogUrl || null,
    ogSiteName: values.ogSiteName || null,
  };
};

export const validationSchema = Yup.object()
  .shape({
    address: locationSchema,
    attractions: Yup.array().test(
      'attractions-validation',
      'Добавьте от 4 до 8 достопримечательностей*',
      function (value) {
        if (!value || value.length === 0) {
          return true;
        }
        return value.length >= 4 && value.length <= 8;
      }
    ),
    category: Yup.mixed().required(),
    contacts: contactsSchema,
    content: contentSchema,
    externalUrl: Yup.string().url(),
    gallery: Yup.array().of(imageSchema),
    getThere: Yup.array().of(
      Yup.object().shape({
        selectedWay: Yup.mixed().required(),
        description: Yup.mixed(),
      })
    ),
    hotels: Yup.array().test(
      'hotels-validation',
      'Добавьте от 4 до 8 отелей*',
      function (value) {
        if (!value || value.length === 0) {
          return true;
        }

        return value.length >= 4 && value.length <= 8;
      }
    ),
    image: imageSchema,
    intro: Yup.string()
      .notRequired()
      .trim()
      .max(700, 'Введите не более 600 символов'),
    name: Yup.string()
      .trim()
      .min(1, 'Введите от 1 до 255 символов')
      .max(255, 'Введите от 1 до 255 символов')
      .required(),
    restaurants: Yup.array().test(
      'restaurants-validation',
      'Добавьте от 4 до 8 ресторанов*',
      function (value) {
        if (!value || value.length === 0) {
          return true;
        }

        return value.length >= 4 && value.length <= 8;
      }
    ),
    shortDescription: Yup.string()
      .trim()
      .min(1, 'Введите от 1 до 300 символов')
      .max(300, 'Введите от 1 до 300 символов')
      .required(),
    status: Yup.mixed().required(),

    tags: Yup.array().max(10, 'Выберите не более ${max} тегов'),

    ogTitle: Yup.string()
      .trim()
      .min(1, 'Введите от 1 до 300 символов')
      .max(300, 'Введите от 1 до 300 символов')
      .required(),
    ogDescription: Yup.string()
      .trim()
      .min(1, 'Введите от 1 до 300 символов')
      .max(300, 'Введите от 1 до 300 символов')
      .required(),
    metaTitle: Yup.string()
      .trim()
      .min(1, 'Введите от 1 до 300 символов')
      .max(300, 'Введите от 1 до 300 символов')
      .required(),
    metaDescription: Yup.string()
      .trim()
      .min(1, 'Введите от 1 до 300 символов')
      .max(300, 'Введите от 1 до 300 символов')
      .required(),
    ogUrl: Yup.string()
      .trim()
      .min(1, 'Введите от 1 до 300 символов')
      .max(300, 'Введите от 1 до 300 символов')
      .required(),
    ogSiteName: Yup.string()
      .trim()
      .min(1, 'Введите от 1 до 300 символов')
      .max(300, 'Введите от 1 до 300 символов')
      .required(),

    workTime: Yup.object().shape({
      mon: scheduleItemSchema,
      tue: scheduleItemSchema,
      wed: scheduleItemSchema,
      thu: scheduleItemSchema,
      fri: scheduleItemSchema,
      sat: scheduleItemSchema,
      sun: scheduleItemSchema,
      comment: Yup.string().max(255, 'Введите от 1 до 255 символов'),
    }),
  })
  .concat(seoValidationSchema);

export const mapValues = (values): IResortItem => {
  return {
    address: mapAddress(values.address),
    attractions: mapDataToCompact(values.attractions) || null,
    category: mapSelect(values.category) as number,
    contacts: mapContacts(values.contacts),
    content: mapContent(values.content),
    externalUrl: values.externalUrl || null,
    gallery: mapGallery(values.gallery),
    getThere: values.getThere?.length ? mapGetThere(values.getThere) : [],
    heroImage: null,
    hotels: mapDataToCompact(values.hotels) || null,
    image: mapImage(values.image),
    intro: values?.intro?.trim() || '',
    lang: mapSelect(values.lang) as LangEnumKeys,
    metaDescription: values.metaDescription || null,
    metaKeywords: values.metaKeywords || null,
    metaTitle: values.metaTitle || null,
    name: values.name.trim(),
    ogDescription: values.ogDescription || null,
    ogTitle: values.ogTitle || null,
    restaurants: mapDataToCompact(values.restaurants) || null,
    shortDescription: values.shortDescription.trim(),
    sortPriority: values.sortPriority || null,
    sortPriorityMainPage: values.sortPriority || null,
    status: mapSelect(values.status) as Statuses,
    tags: mapSelect(values.tags) as number[],
    workTime: values.workTime,
    ogImage: values.ogImage ? mapImageForSeo(mapImage(values.ogImage)) : null,
    ogType: values.ogType || null,
    ogUrl: values.ogUrl || null,
    ogSiteName: values.ogSiteName || null,
  };
};

export const mapValuesToDraft = (values): IResortDraftItem => {
  return {
    address:
      values.address?.city || values.address?.settlement
        ? mapAddress(values.address)
        : null,
    attractions: Object.values(values.attractions) || null,
    category: values.category ? (mapSelect(values.category) as number) : null,
    contacts: values.contacts?.length > 0 ? values.contacts : [],
    content: values.content?.length ? mapContent(values.content) : [],
    externalUrl: values.externalUrl || null,
    gallery: values.gallery?.length ? mapGallery(values.gallery) : [],
    heroImage: null,
    hotels: Object.values(values.hotels) || null,
    intro: values?.intro?.trim() || '',
    image: values.image ? mapImage(values.image) : null,
    lang: mapSelect(values.lang) as LangEnumKeys,
    metaDescription: values.metaDescription || null,
    metaKeywords: values.metaKeywords || null,
    metaTitle: values.metaTitle || null,
    name: values.name?.trim() || null,
    ogDescription: values.ogDescription || null,
    ogTitle: values.ogTitle || null,
    restaurants: Object.values(values.restaurants) || null,
    shortDescription: values.shortDescription?.trim() || null,
    sortPriority: values.sortPriority || null,
    sortPriorityMainPage: values.sortPriority || null,
    tags: values.tags?.length ? (mapSelect(values.tags) as number[]) : [],
    workTime: values.workTime,
    ogImage: values.ogImage ? mapImageForSeo(mapImage(values.ogImage)) : null,
    ogType: values.ogType || null,
    ogUrl: values.ogUrl || null,
    ogSiteName: values.ogSiteName || null,
  };
};
